import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Wrench } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

/**
 * Global maintenance banner. Polls ``/api/platform/status`` every 60 s and
 * displays a fixed amber strip when the platform is in maintenance mode.
 *
 * Superadmins keep write access (they are the ones who need to unlock the
 * platform) so we still show the banner to them, in a "read-only for others"
 * shade, to make the state obvious.
 */
export default function MaintenanceBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ maintenance: false, message: "" });

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/platform/status");
        if (mounted) setStatus(data);
      } catch {
        /* ignore — offline */
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 60_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (!status.maintenance) return null;
  const isSuperadmin = !!user?.is_superadmin;

  return (
    <div
      className="w-full bg-amber-500 text-amber-950 px-6 py-2.5 flex items-center gap-3 sticky top-0 z-40 shadow-sm"
      data-testid="maintenance-banner"
    >
      <Wrench size={18} weight="bold" className="shrink-0" />
      <div className="flex-1 text-sm">
        <strong>Plateforme en maintenance.</strong>{" "}
        {isSuperadmin
          ? "Écritures bloquées pour les autres utilisateurs — vous conservez l'accès."
          : "Les enregistrements sont temporairement indisponibles."}
        {status.message ? ` — ${status.message}` : ""}
      </div>
    </div>
  );
}
